import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { appRoutes } from "@/routesConfig";
import { usePageMeta, type PageMetaInput } from "@/hooks/usePageMeta";
import { matchesRoutePath } from "@/utils/routePath";

/** Keep in sync with <title> in index.html (the shell default). */
const SHELL_DEFAULT_TITLE = "Race Manager — Your racing day, simplified.";

function metaForPath(pathname: string): PageMetaInput | undefined {
  for (const route of appRoutes) {
    if (route.meta && matchesRoutePath(route.path, pathname)) {
      return route.meta;
    }
  }
  return undefined;
}

/**
 * Applies the static per-route document title/description declared in
 * routesConfig on every navigation. Rendered next to PageTracker so its
 * effects run before page components — pages can then override with
 * dynamic (data-driven) titles via usePageMeta. The pathname resetKey makes
 * param-only navigations (same route, new id) re-assert the static title
 * while the new data loads, and unmatched paths (/, 404s) restore the shell
 * default instead of keeping the previous page's dynamic title.
 */
export default function RouteMeta() {
  const { pathname } = useLocation();
  const meta = metaForPath(pathname);
  usePageMeta(meta ?? {}, pathname);

  useEffect(() => {
    if (!meta) {
      document.title = SHELL_DEFAULT_TITLE;
    }
  }, [meta, pathname]);

  return null;
}
